import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  UploadOutlined,
  DeleteOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import PaginationTable from "../../../shared/PaginationTable";
// import { useNavigate } from "react-router-dom";
import moment from 'moment';
import {
  Button,
  Input,
  Col,
  Row,
  Progress,
  Form,
  message,
  Popconfirm,
  Spin,
  Space,
  Upload,
  Empty,
  Modal,
  Divider,
  Typography,
  Tooltip,
  Skeleton,
  Switch,
  Select,
  Popover,
  Badge,
  Tag,
  Card,
  Table,
} from "antd";
import Papa from "papaparse";
import {
  encryptStringSHA512,
  encryptStringSHA256,
  encryptStringSHA3,
  encryptStringSHA1,
  encryptStringMD5,
} from "../../../../core/utils/encryptString";
import TemplateCSVDownloader from "../../../shared/TemplateCSVDownloader";
import { HashedEmailConfig } from "../configs/emailConfig";
import GuidedWorkflowSteps from "../../FirstPartyData/components/GuidedWorkflowSteps";
import { useUploadProgress } from "../../../../core/components/firstPartyData/providers/UploadProgressProvider";
const { Dragger } = Upload;
const { Text, Title } = Typography;

// Helper function to map DSP names based on providersVisible permission
const getDSPDisplayName = (dspName, providersVisible) => {
  if (providersVisible) {
    return dspName; // Show actual DSP name
  }
  
  // Map to tier names when providersVisible is false
  switch (dspName?.toLowerCase()) {
    case 'liquidm':
      return 'Tier 1';
    case 'nexxen':
      return 'Tier 2';
    default:
      return dspName; // Return original if not mapped
  }
};

const EmailTab = ({
  activateHashedEmailList,
  createHashedEmailList,
  updateHashedEmailList,
  bulkCreateHashedEmailAddress,
  hashedEmailLists,
  deleteHashedEmailList,
  refetchHashedEmailLists,
  loadingHashedEmailList,
  processDeviceIds,
  processModelScore,
  updateHashedEmailLatestStatus,
  currentOrg,
  searchTerm = "",
  setSearchTerm = () => {},
  selectedAdvertiserId,
  setSelectedAdvertiserId,
  allAdvertisers = [],
  providersVisible,
  pagination = { current: 1, pageSize: 50, total: 0 },
  setPagination = () => {},
}) => {
  const [form] = Form.useForm();
  const { uploadState } = useUploadProgress();
  const [isUploading, setIsUploading] = useState(false);
  const [isTableBusy, setIsTableBusy] = useState(false);
  const [isUploadComplete, setUploadComplete] = useState(false);
  const [isEmailsProcessed, setEmailsProcessed] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showUploadModal, setSetShowModal] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [emails, setEmails] = useState([]);
  const [savedHashedEmailCount, setHashedEmailCount] = useState(0);
  const [emailsAlreadyHashed, setEmailsAlreadyHashed] = useState(false);
  const [selectedHashingMethod, setSelectedHashingMethod] = useState('');
  const [visible, setModalVisibility] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [sortOrder, setSortOrder] = useState("ascending");
  const [sortValue, setSortValue] = useState("descend");
  const [showGuidedWorkflow, setShowGuidedWorkflow] = useState(false);
  const [bulkStatusLoading, setBulkStatusLoading] = useState(false);
  const [statusResultsModal, setStatusResultsModal] = useState(false);
  const [statusResults, setStatusResults] = useState(null);
  const [processModelScoreModal, setProcessModelScoreModal] = useState(false);
  const [modelScoreResults, setModelScoreResults] = useState(null);
  const [processingModelScoreId, setProcessingModelScoreId] = useState(null);
  const emailRegex = /\S+@\S+\.\S+/;

  useEffect(() => {
    const handleResizeObserverError = (event) => {
      if (
        event?.message === "ResizeObserver loop completed with undelivered notifications." ||
        event?.message === "ResizeObserver loop limit exceeded"
      ) {
        event.stopImmediatePropagation();
      }
    };

    window.addEventListener("error", handleResizeObserverError);
    return () => window.removeEventListener("error", handleResizeObserverError);
  }, []);

  const handleGetAllLatestStatus = async () => {
    try {
      setBulkStatusLoading(true);
      
      if (!currentOrg || !currentOrg.id || !currentOrg.name) {
        return message.error("Organization information not available");
      }

      const response = await updateHashedEmailLatestStatus({
        variables: {
          orgID: currentOrg.id
        }
      });

      if (response && response.data && response.data.updateHashedEmailLatestStatus) {
        let result;
        try {
          // Handle both string and object responses
          if (typeof response.data.updateHashedEmailLatestStatus === 'string') {
            result = JSON.parse(response.data.updateHashedEmailLatestStatus);
          } else {
            result = response.data.updateHashedEmailLatestStatus;
          }
        } catch (parseError) {
          console.error("Error parsing response:", parseError);
          message.error("Error parsing server response");
          return;
        }
        
        if (result && (result.processed !== undefined || result.hashedEmailLists || result.data)) {
          // Show appropriate message based on results
          if (result.errorCount > 0 && result.successCount > 0) {
            // Mixed results
            message.warning(`${result.message || 'Status update completed with mixed results'}. Processed: ${result.processed || 0}, Successful: ${result.successCount || 0}, Failed: ${result.errorCount || 0}`);
          } else if (result.errorCount > 0) {
            // All failed
            message.error(`${result.message || 'Status update failed'}. Processed: ${result.processed || 0}, Failed: ${result.errorCount || 0}`);
          } else if (result.successCount > 0) {
            // All successful
            message.success(`${result.message || 'Status updated successfully'}. Processed: ${result.processed || 0}, Successful: ${result.successCount || 0}`);
          } else {
            // No changes (e.g., all waiting for activation)
            message.info(`${result.message || 'Status check completed'}. Processed: ${result.processed || 0}. ${result.message === 'Waiting for Activation' ? 'All items are waiting for activation.' : 'No status changes detected.'}`);
          }
          
          // Transform new response format to match existing modal structure
          const transformedResult = {
            ...result,
            // Map the new 'data' array to 'hashedEmailLists' for backward compatibility
            hashedEmailLists: result.data || result.hashedEmailLists || [],
            // Ensure we have the expected summary fields
            processed: result.data?.length || result.processed || 0,
            successCount: result.data?.length || result.successCount || 0,
            errorCount: result.errorCount || 0
          };
          
          // Always store transformed results and show modal if we have data
          setStatusResults(transformedResult);
          setStatusResultsModal(true);
          
          // Refresh the audience list to show updated statuses
          if (refetchHashedEmailLists) {
            await refetchHashedEmailLists();
          }
        } else {
          message.error(result?.error || result?.message || "Failed to update statuses");
        }
      } else {
        message.warning("No response received from server");
      }
    } catch (error) {
      message.error("Error updating all latest statuses: " + error.message);
      console.error("Error in handleGetAllLatestStatus:", error);
    } finally {
      setBulkStatusLoading(false);
    }
  };

  const handleProcessModelScore = async (record) => {
    try {
      setProcessingModelScoreId(record.id);

      if (!currentOrg || !currentOrg.id) {
        return message.error("Organization information not available");
      }

      const response = await processModelScore({
        variables: {
          orgID: currentOrg.id,
          hashedEmailListId: record.id
        }
      });

      if (response && response.data && response.data.processModelScore) {
        let result;

        // Check if response is already an object or needs parsing
        if (typeof response.data.processModelScore === 'string') {
          try {
            result = JSON.parse(response.data.processModelScore);
          } catch (parseError) {
            console.error("Error parsing response:", parseError);
            // If parsing fails, try to use the raw response
            result = {
              success: false,
              message: "Error parsing server response",
              rawResponse: response.data.processModelScore
            };
          }
        } else {
          // Response is already an object
          result = response.data.processModelScore;
        }

        // Check the success field to determine if operation was successful
        if (result && result.success) {
          message.success(result.message || "Model score processed successfully");
          setModelScoreResults(result);
          setProcessModelScoreModal(true);

          // Refresh the audience list
          if (refetchHashedEmailLists) {
            await refetchHashedEmailLists();
          }
        } else {
          message.error(result?.message || "Failed to process model score");
          // Still show modal with error message
          setModelScoreResults(result);
          setProcessModelScoreModal(true);
        }
      } else {
        message.warning("No response received from server");
      }
    } catch (error) {
      message.error("Error processing model score: " + error.message);
      console.error("Error in handleProcessModelScore:", error);
    } finally {
      setProcessingModelScoreId(null);
    }
  };

  const history = useHistory();

  const sortIcon =
    sortOrder === 'ascending' ? <SortAscendingOutlined /> : <SortDescendingOutlined />;

  const hashedEmailListExtracted =
    !Array.isArray(hashedEmailLists) &&
    hashedEmailLists &&
    hashedEmailLists.getHashedEmailListWithCount.hashedEmailList
      ? hashedEmailLists.getHashedEmailListWithCount.hashedEmailList
      : [];

  console.log('EmailTab - hashedEmailListExtracted length:', hashedEmailListExtracted.length);
  console.log('EmailTab - pagination:', pagination);

  // Use cached advertisers if available, otherwise extract from current data
  const uniqueAdvertisers = React.useMemo(() => {
    // If we have a cached list of all advertisers, use that
    if (allAdvertisers && allAdvertisers.length > 0) {
      return allAdvertisers;
    }
    
    // Otherwise, extract from current data (for OrgConfig use case)
    const advertisersMap = new Map();
    
    hashedEmailListExtracted.forEach(item => {
      if (item.orgs && item.orgs.length > 0) {
        const org = item.orgs[0];
        if (!advertisersMap.has(org.id)) {
          advertisersMap.set(org.id, {
            id: org.id,
            name: org.name
          });
        }
      }
    });
    
    return Array.from(advertisersMap.values()).sort((a, b) => 
      a.name.localeCompare(b.name)
    );
  }, [allAdvertisers, hashedEmailListExtracted]);

  const emailModalColumn = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text) => {
        if (isEmailsProcessed && !emailsAlreadyHashed) {
          const isValidEmail = emailRegex.test(text);
          return (
            <span
              style={{
                color: isValidEmail ? 'inherit' : 'red',
              }}
            >
              {text} {isValidEmail ? '' : '(Invalid Value)'}
            </span>
          );
        } else if (isEmailsProcessed && emailsAlreadyHashed) {
          const isEmail = emailRegex.test(text);
          return (
            <span
              style={{
                color: !isEmail ? 'inherit' : 'red',
              }}
            >
              {text} {!isEmail ? '' : '(Invalid Value)'}
            </span>
          );
        } else {
          return <span>{text}</span>;
        }
      },
      sorter: (a, b) => {
        const isValidA = emailsAlreadyHashed ? !emailRegex.test(a.email) : emailRegex.test(a.email);
        const isValidB = emailsAlreadyHashed ? !emailRegex.test(b.email) : emailRegex.test(b.email);

        if (isValidA && !isValidB) {
          return -1; // valid emails come before invalid emails
        } else if (!isValidA && isValidB) {
          return 1; // invalid emails come after valid emails
        } else {
          return 0; // maintain the original order
        }
      },
      defaultSortOrder: 'descend',
      sortOrder: sortValue,
    },
    {
      title: 'Option',
      dataIndex: 'option',
      key: 'option',
      render: (_, email) => (
        <Popconfirm
          title="Are you sure you want to delete this email?"
          onConfirm={async (e) => {
            const index = emails.findIndex((element) => element === email.email);
            if (index !== -1) {
              const newData = [...emails]; // create a new array with the same elements as the state array
              newData.splice(index, 1); // remove the element at the found index
              setEmails(newData); // set the state to the new array
              message.success('Email deleted');
            }
          }}
          onCancel={(e) => {
            message.warning('Cancelled');
          }}
          okText="Yes"
          cancelText="No"
        >
          <DeleteOutlined />
        </Popconfirm>
      ),
    },
  ];


  const columns = [
    {
      title: "Audience Name",
      dataIndex: "name",
      key: "name",
      width: 180,
      ellipsis: {
        showTitle: false,
      },
      render: text => (
        <Tooltip title={text} placement="topLeft">
          <span
            style={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </span>
        </Tooltip>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Advertiser",
      dataIndex: "advertiser",
      key: "advertiser",
      width: 150,
      ellipsis: {
        showTitle: false,
      },
      render: (_, record) => {
        const advertiserName = record.orgs && record.orgs.length > 0
          ? record.orgs[0].name
          : "N/A";
        return (
          <Tooltip title={advertiserName} placement="topLeft">
            <span style={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}>
              {advertiserName}
            </span>
          </Tooltip>
        );
      },
      sorter: (a, b) => {
        const aAdvertiser = a.orgs && a.orgs.length > 0 ? a.orgs[0].name : "";
        const bAdvertiser = b.orgs && b.orgs.length > 0 ? b.orgs[0].name : "";
        return aAdvertiser.localeCompare(bAdvertiser);
      },
      responsive: ['md'],
    },
    {
      title: "Count Information",
      dataIndex: "countInformation",
      key: "countInformation",
      width: 220,
      render: (_, record) => {
        const hashedCount = record.tag ? parseInt(record.tag) : 0;
        const ingestedDevices = Math.ceil(record.deviceCount) || 0;
        const matchRate = record.matchRate !== null && record.matchRate !== undefined
          ? record.matchRate.toFixed(2)
          : "0.00";

        return (
          <div style={{ fontSize: "12px", lineHeight: "1.6" }}>
            <div>
              <strong>Hashed Count:</strong> {hashedCount.toLocaleString()}
            </div>
            <div>
              <strong>Ingested Devices:</strong> {ingestedDevices.toLocaleString()}
            </div>
            <div>
              <strong>Match Rate:</strong> {matchRate}%
            </div>
          </div>
        );
      },
      sorter: (a, b) => {
        const aTag = a.tag ? parseInt(a.tag) : 0;
        const bTag = b.tag ? parseInt(b.tag) : 0;
        return aTag - bTag;
      },
      responsive: ['md'],
    },
    {
      title: "Dates",
      dataIndex: "dates",
      key: "dates",
      width: 230,
      render: (_, record) => {
        const createdDate = record.createdAt ? moment(record.createdAt).format("MM/DD/YYYY h:mm A") : "N/A";

        let lastUpdatedDate = "Never";
        if (record.processedAt && record.createdAt) {
          if (moment(record.processedAt).valueOf() !== moment(record.createdAt).valueOf()) {
            lastUpdatedDate = moment(record.processedAt).format("MM/DD/YYYY h:mm A");
          }
        }

        return (
          <div style={{ fontSize: "12px", lineHeight: "1.6" }}>
            <div>
              <strong>Created:</strong> {createdDate}
            </div>
            <div>
              <strong>Last Updated:</strong> {lastUpdatedDate}
            </div>
          </div>
        );
      },
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
      responsive: ['lg'],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 220,
      render: (text, record) => {
        // Check if this is the currently uploading audience
        const isUploading = uploadState &&
                           uploadState.status === "uploading" &&
                           uploadState.emailListId === record.id;

        const status = isUploading ? "UPLOADING" : (record.status || (record.activated ? "ACTIVATED" : null));
        const formattedStatus = status ? status.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) : "No Status";

        // Status color mapping
        const getStatusColor = (status) => {
          if (!status) return "default";
          switch (status) {
            case "UPLOADING":
              return "blue";
            case "UPLOADED":
              return "processing";
            case "WAITING_FOR_ACTIVATION":
              return "warning";
            case "ACTIVATED":
              return "success";
            case "NO_ACTION":
              return "default";
            default:
              return "default";
          }
        };

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <Tag
              color={getStatusColor(status)}
              style={{
                margin: 0,
                borderRadius: "6px",
                fontSize: "11px",
                lineHeight: "20px",
                minWidth: "fit-content",
                whiteSpace: "nowrap"
              }}
            >
              {isUploading && <LoadingOutlined style={{ marginRight: "4px" }} />}
              {formattedStatus}
            </Tag>
            {status === "WAITING_FOR_ACTIVATION" && record.stepProgress && (
              <Badge
                count={`${record.stepProgress}/4`}
                style={{ backgroundColor: '#1890ff' }}
              />
            )}
            {isUploading && uploadState.progress !== undefined && (
              <Badge
                count={`${uploadState.progress}%`}
                style={{ backgroundColor: '#1890ff' }}
              />
            )}
            {record.refreshStatus === "NEEDS_REFRESH" && (
              <Tag
                color="warning"
                style={{
                  margin: 0,
                  borderRadius: "6px",
                  fontSize: "10px",
                  lineHeight: "18px",
                }}
              >
                Needs Refresh
              </Tag>
            )}
            {record.refreshStatus === "REFRESHING" && (
              <Tag
                color="processing"
                icon={<SyncOutlined spin />}
                style={{
                  margin: 0,
                  borderRadius: "6px",
                  fontSize: "10px",
                  lineHeight: "18px",
                }}
              >
                Refreshing
              </Tag>
            )}
            {record.refreshStatus === "REFRESH_FAILED" && (
              <Tag
                color="error"
                style={{
                  margin: 0,
                  borderRadius: "6px",
                  fontSize: "10px",
                  lineHeight: "18px",
                }}
              >
                Refresh Failed
              </Tag>
            )}
          </div>
        );
      },
      sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
    },
    {
      title: "Data Type",
      dataIndex: "typeOfFirstPartyData",
      key: "typeOfFirstPartyData",
      width: 180,
      render: (_, record) => {
        const typeText = record.typeOfFirstPartyData === "phones"
          ? "Phone Numbers"
          : record.typeOfFirstPartyData === "lookalike"
            ? "Lookalike Audiences"
            : "Email Addresses";
        return (
          <span style={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}>
            {typeText}
          </span>
        );
      },
      sorter: (a, b) => {
        const getTypeLabel = (type) => {
          if (type === "phones") return "Phone Numbers";
          if (type === "lookalike") return "Lookalike Audiences";
          return "Email Addresses";
        };
        const aType = getTypeLabel(a.typeOfFirstPartyData);
        const bType = getTypeLabel(b.typeOfFirstPartyData);
        return aType.localeCompare(bType);
      },
      responsive: ['lg'],
    },
    {
      title: "DSP",
      dataIndex: "dsp",
      key: "dsp",
      width: 90,
      render: (_, record) => {
        const dspName = record.dspSegmentIds && record.dspSegmentIds.length
          ? record.dspSegmentIds[0].dsp
          : record.dsp;
        const displayName = getDSPDisplayName(dspName, providersVisible);
        return displayName && typeof displayName === "string" ? displayName.toUpperCase() : "N/A";
      },
      sorter: (a, b) => {
        const aDsp = a.dspSegmentIds && a.dspSegmentIds.length ? a.dspSegmentIds[0].dsp : a.dsp;
        const bDsp = b.dspSegmentIds && b.dspSegmentIds.length ? b.dspSegmentIds[0].dsp : b.dsp;
        return (aDsp || "").localeCompare(bDsp || "");
      },
    },
    {
      title: "Segment Information",
      dataIndex: "segmentInformation",
      key: "segmentInformation",
      width: 250,
      render: (_, record) => {
        const segmentInfo =
          record.dspSegmentIds && record.dspSegmentIds.length
            ? record.dspSegmentIds[0]
            : { segmentId: "", segmentName: "" };

        // Check if both ID and Name are empty/null to show blank
        const hasSegmentId = segmentInfo.segmentId && segmentInfo.segmentId.trim() !== "";
        const hasSegmentName = segmentInfo.segmentName && segmentInfo.segmentName.trim() !== "";

        if (!hasSegmentId && !hasSegmentName) {
          return "";
        }

        return (
          <div style={{ fontSize: "12px", lineHeight: "1.5" }}>
            <div style={{
              wordBreak: "break-word",
              whiteSpace: "normal",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}>
              <strong>ID:</strong> {segmentInfo.segmentId || "N/A"}
            </div>
            <div style={{
              wordBreak: "break-word",
              whiteSpace: "normal",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}>
              <strong>Name:</strong> {segmentInfo.segmentName || "N/A"}
            </div>
          </div>
        );
      },
      responsive: ['xl'],
    },
  ];

  const removeDuplicates = (arr) => {
    return Array.from(new Set(arr));
  };

  const convertToLowercase = (arr) => {
    return arr.map((e) => e.toLowerCase());
  };

  const containsEmail = (arr) => {
    return arr.some((element) => emailRegex.test(element));
  };

  const findInvalidEmail = (arr) => {
    return arr.find((element) => !emailRegex.test(element)) || null;
  };

  function hasNonHashedValue(arr) {
    const hexRegex = /^[0-9a-fA-F]+$/;
    return !arr.every((str) => {
      return (
        (str.length === 32 || str.length === 40 || str.length === 64 || str.length === 128) &&
        hexRegex.test(str) &&
        str !== str.split('').reverse().join('')
      );
    });
  }

  const startUpload = async () => {
    let encryptedEmails = [];

    if (isNameEmpty()) {
      return message.error('Please input list name.');
    }

    if (!isNameValid()) {
      return message.error(
        'Name can only contain letters, numbers, dash (-) and underscore (_). No spaces or other special characters allowed.'
      );
    }

    if (emailsAlreadyHashed && (containsEmail(emails) || hasNonHashedValue(emails))) {
      return message.warning(
        'One or more of the values in your data contains an email address. Please ensure that all values are hashed or use an email-free dataset.'
      );
    }

    if (!emailsAlreadyHashed && findInvalidEmail(emails)) {
      return message.warning(
        'One or more of the values in your data is not a valid email address. Please correct your data or use an email-free dataset.'
      );
    }

    if (!emailsAlreadyHashed) {
      encryptedEmails = emails.map((email) => encryptStringBasedOnHashSelection(email));
      setEmails(encryptedEmails);
    } else {
      encryptedEmails = emails;
    }

    setEmailsProcessed(false);
    setIsUploading(true);

    const chunkSize =
      selectedHashingMethod === 'SHA3' || selectedHashingMethod === 'SHA512' ? 500 : 1000;

    let progressCount = 0;
    let isLastIteration = false;

    const response = await createHashedEmailList({
      name: form.getFieldValue('name'),
    });

    const emailListId = response.data.createHashedEmailList.id;

    for (let i = 0; i < encryptedEmails.length; i += chunkSize) {
      const chunk = encryptedEmails.slice(i, i + chunkSize);
      progressCount += chunkSize;

      isLastIteration = i + chunkSize >= encryptedEmails.length;
      const response = await bulkCreateHashedEmailAddress({
        emailListId: emailListId,
        emails: chunk,
        refetch: isLastIteration,
      });

      setHashedEmailCount(
        (prevCount) => prevCount + response.data.bulkCreateHashedEmailAddress.uniqueEmails
      );

      setProgress(Math.floor((progressCount / encryptedEmails.length) * 100));
    }

    setIsUploading(false);
    setUploadComplete(true);
    refetchHashedEmailLists();
  };

  const activateAllEmails = async (data) => {
    setIsUploading(true);
    const response = await activateHashedEmailList({});
    if (
      response &&
      response.data &&
      response.data.activateHashedEmailList &&
      response.data.activateHashedEmailList.success
    ) {
      message.success('Emails Activated');
      await refetchHashedEmailLists();
    } else if (
      response &&
      response.data &&
      response.data.activateHashedEmailList &&
      response.data.activateHashedEmailList.error
    ) {
      message.error(response.data.activateHashedEmailList.error);
    } else {
      message.error(
        "No device IDs were processed. Please make sure there are emails to process."
      );
    }

    setIsUploading(false);
  };

  const changeHashingMethod = (hashingMethod) => {
    setSelectedHashingMethod(hashingMethod);
  };

  const handleEmailHashToggle = (checked) => {
    setEmailsAlreadyHashed(checked);
    setSortValue('descend');
  };

  const handleSortByName = () => {
    let sorted;
    if (sortOrder === 'ascending') {
      sorted = [...emails].sort((a, b) => a.localeCompare(b));
      setSortOrder('descending');
    } else {
      sorted = [...emails].sort((a, b) => b.localeCompare(a));
      setSortOrder('ascending');
    }
    setEmails(sorted);
  };

  const encryptStringBasedOnHashSelection = (value) => {
    switch (selectedHashingMethod) {
      case 'SHA512':
        return encryptStringSHA512(value);
      case 'SHA256':
        return encryptStringSHA256(value);
      case 'SHA3':
        return encryptStringSHA3(value);
      case 'SHA1':
        return encryptStringSHA1(value);
      case 'MD5':
        return encryptStringMD5(value);
      default:
        return encryptStringSHA256(value);
    }
  };

  const customRequest = ({ file, onError }) => {
    if (file.name.toLowerCase().includes('csv')) {
      Papa.parse(file, {
        skipEmptyLines: 'greedy',
        error: function (err, file, inputElem, reason) {
          onError({ err, file, inputElem, reason });
        },
        complete: async (results) => {
          const currentEmails = removeDuplicates(results.data.flat(4));
          const lowerCaseEmails = convertToLowercase(currentEmails);
          const combinedEmails = [...emails, ...lowerCaseEmails];
          const uniqueEmails = removeDuplicates(combinedEmails);
          setEmails(uniqueEmails);
        },
      });
    } else {
      message.error('Invalid file type. Only accepts .csv');
      return;
    }
  };

  const handleCloseUploadModal = () => {
    form.resetFields();
    setEmails([]);
    setErrorText('');
    setProgress(0);
    setHashedEmailCount(0);
    setUploadComplete(false);
    setSetShowModal(false);
    setEmailsAlreadyHashed(false);
  };

  const isNameEmpty = () => {
    const nameValue = form.getFieldValue('name');
    return !nameValue || nameValue.trim() === '';
  };

  const isNameValid = () => {
    const nameValue = form.getFieldValue('name');
    if (!nameValue || nameValue.trim() === '') return false;
    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    return validNameRegex.test(nameValue.trim());
  };

  const [selectedRows, setSelectedRows] = useState([]);

  const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => {
      // Check if this is the currently uploading audience
      const isUploading = uploadState &&
                         uploadState.status === "uploading" &&
                         uploadState.emailListId === record.id;

      return {
        disabled: isUploading || record.status === "ACTIVATED" || record.status === "WAITING_FOR_ACTIVATION" || record.status === "NO_ACTION",
      };
    },
  };

  const handleBatchDelete = async () => {
    try {
      setIsTableBusy(true);
      const hasProtectedItem = selectedRows.some(row =>
        row.status === "WAITING_FOR_ACTIVATION" || row.status === "ACTIVATED" || row.status === "NO_ACTION"
      );

      if (hasProtectedItem) {
        message.warning(
          "One or more of the selected email lists are in progress or already completed. Please deselect them and try again."
        );
        return;
      }
      // Map each row to a delete promise
      const deletePromises = selectedRows.map(async (row) => {
        return deleteHashedEmailList(row); // Return the promise for deletion
      });

      // Wait for all promises to resolve
      await Promise.all(deletePromises);

      await refetchHashedEmailLists();

      message.success('All selected email lists have been successfully deleted.');
    } catch (error) {
      message.error('An error occurred while deleting the email lists.');
    } finally {
      setSelectedRows([]);
      setSelectedRowKeys([]);
      setIsTableBusy(false);
      setIsUploading(false);
    }
  };

  // Handle table pagination and sorting changes
  const handleTableChange = (paginationOptions, filters, sorter) => {
    console.log('handleTableChange called with:', paginationOptions);
    console.log('setPagination function:', setPagination);

    // Reset to page 1 if page size changed
    const targetPage = paginationOptions.pageSize !== pagination.pageSize ? 1 : paginationOptions.current;

    console.log('Setting pagination to:', { current: targetPage, pageSize: paginationOptions.pageSize, total: paginationOptions.total });

    setPagination({
      current: targetPage,
      pageSize: paginationOptions.pageSize,
      total: paginationOptions.total || pagination.total,
    });

    console.log('After setPagination called');
  };

  return (
    <>
      <Modal
        title="Email Upload"
        visible={showUploadModal}
        onCancel={handleCloseUploadModal}
        closable={!isUploading}
        maskClosable={false}
        keyboard={false}
        style={{ top: 20 }}
        bodyStyle={{ height: '79vh', overflowY: 'scroll' }}
        width="80%"
        footer={[
          <Button
            key="back"
            onClick={handleCloseUploadModal}
            disabled={isUploading}
            icon={<CloseOutlined />}
          >
            Close
          </Button>,
          <Button
            key="ok"
            type="primary"
            htmlType="submit"
            onClick={startUpload}
            disabled={
              emails.length === 0 || isUploadComplete === true || isUploading
            }
            icon={<UploadOutlined />}
          >
            Upload
          </Button>,
        ]}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Form
            form={form}
            name="basic"
            initialValues={{
              remember: true,
            }}
            autoComplete="off"
            onFinish={startUpload}
            style={{ marginBottom: '0px', width: '60%' }}
          >
            <Form.Item
              label="Name"
              name="name"
              style={{ marginBottom: '0px' }}
              rules={[
                { required: true, message: 'Please input your list name.' },
                {
                  pattern: /^[a-zA-Z0-9_-]+$/,
                  message:
                    'Name can only contain letters, numbers, dash (-) and underscore (_). No spaces or other special characters allowed.',
                },
              ]}
            >
              <Input
                placeholder="Email List Name"
                style={{
                  width: '100%',
                  alignSelf: 'flex-start',
                }}
                disabled={isUploading}
              />
            </Form.Item>
          </Form>
          <TemplateCSVDownloader
            tooltipMessage="CSV Template for Email list"
            rows={HashedEmailConfig.csvTemplate.rows}
            templateName={HashedEmailConfig.csvTemplate.templateName}
            styles={{ margin: '0px', height: '34px' }}
            title="Sample Email list template"
          ></TemplateCSVDownloader>
        </div>

        <div style={{ marginTop: '0.2em' }}>
          <Popover
            content={
              <ol style={{ fontSize: '14px' }}>
                <li>Enter the Email List Name</li>
                <li>
                  Upload a CSV file. Please make sure to remove the header in the column. Download
                  the Sample Template for reference
                </li>
                <li>
                  Select your desired options
                  <ol>
                    <li>
                      If your CSV contains hashed email, turn on the option for emails already being
                      hashed
                    </li>
                    <li>Alternatively, select your desired hashing method</li>
                  </ol>
                </li>
                <li>Click on the Upload button</li>
              </ol>
            }
            title="Instructions on how to use the Email Uploader feature"
          >
            <Button type="default" size="small" icon={<InfoCircleOutlined />}>
              Hover to see Instructions
            </Button>
          </Popover>
        </div>

        <Divider />

        {(isUploading === true || isUploadComplete === true) && (
          <>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Progress percent={progress} status={errorText ? 'exception' : 'active'} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {isUploadComplete === true && (
                <Text type="success" style={{ color: '#52c41a', textAlign: 'center' }}>
                  Upload Complete.{' '}
                  {`${savedHashedEmailCount}/${emails.length} emails were successfully saved.`}
                </Text>
              )}
            </div>
            <div style={{ textAlign: 'center', marginTop: '10px' }}>
              {errorText ? <div style={{ color: 'red' }}> Error: {errorText}</div> : <></>}
            </div>
          </>
        )}

        <div style={{ marginTop: '0.5em' }}>
          <Row justify="space-between" align="top">
            <Col span={12}>
              Emails are already hashed{' '}
              <Tooltip title={'Enable this option if the emails are already hashed.'}>
                <Switch
                  defaultChecked={false}
                  checked={emailsAlreadyHashed}
                  onChange={handleEmailHashToggle}
                />
              </Tooltip>
            </Col>
            <Col span={12} align="right">
              Hashed Method Selection{' '}
              <Select
                defaultValue="SHA256"
                disabled={emailsAlreadyHashed}
                onChange={(_, val) => {
                  changeHashingMethod(val.value);
                }}
                options={[
                  {
                    value: 'SHA512',
                    label: 'SHA512',
                  },
                  {
                    value: 'SHA256',
                    label: 'SHA256',
                  },
                  {
                    value: 'SHA3',
                    label: 'SHA3',
                  },
                  {
                    value: 'SHA1',
                    label: 'SHA1',
                  },
                  {
                    value: 'MD5',
                    label: 'MD5',
                  },
                ]}
              />
            </Col>
          </Row>
        </div>

        <Spin spinning={isUploading} size="large">
          {emails.length === 0 && (
            <>
              <div style={{ marginTop: '2em' }}>
                <Dragger
                  accept={'.csv'}
                  showUploadList={false}
                  multiple={false}
                  customRequest={(params) =>
                    customRequest({
                      ...params,
                    })
                  }
                >
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                  </p>
                  <p className="ant-upload-text">Click or drag CSV file to this area to upload</p>
                  <p className="ant-upload-hint">Import your Emails here.</p>
                </Dragger>
              </div>
            </>
          )}

          {emails.length > 0 && (
            <div
              style={{
                height: '60vh',
                overflowY: 'scroll',
                textAlign: 'right',
              }}
            >
              <Row justify="space-between" align="middle" style={{ marginBottom: '0.4em' }}>
                <Col>
                  <Upload
                    accept={'.csv'}
                    showUploadList={false}
                    multiple={false}
                    customRequest={(params) =>
                      customRequest({
                        ...params,
                      })
                    }
                  >
                    <Button icon={<UploadOutlined />} disabled={isUploadComplete}>
                      Add more
                    </Button>
                  </Upload>
                  <Popconfirm
                    title="Are you sure you want to delete all emails?"
                    onConfirm={() => {
                      setEmails([]);
                      message.success('Emails deleted sucessfully');
                      setModalVisibility(false);
                    }}
                    visible={visible}
                    onCancel={() => {
                      message.warning('Cancelled');
                      setModalVisibility(false);
                    }}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      icon={<DeleteOutlined />}
                      disabled={emails.length === 0}
                      style={{ marginLeft: '0.5em' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setModalVisibility(true);
                      }}
                    >
                      Delete all
                    </Button>
                  </Popconfirm>
                  <Button
                    icon={sortIcon}
                    disabled={emails.length === 0}
                    style={{ marginLeft: '0.5em' }}
                    onClick={() => {
                      handleSortByName();
                    }}
                  >
                    Sort by Name
                  </Button>
                </Col>
                <Col>
                  {emails.length && (
                    <Tooltip
                      title={
                        'Some of the emails in your CSV file are duplicates, and they have been automatically removed.'
                      }
                    >
                      <Text type="secondary" style={{ textAlign: 'right' }}>
                        Email Count: {emails.length}
                      </Text>
                    </Tooltip>
                  )}
                </Col>
              </Row>

              <PaginationTable
                columns={emailModalColumn}
                data={emails.map((email) => {
                  return { email: email };
                })}
                total={emails.length}
                bordered={false}
                paginationSize={'default'}
                size={'small'}
                current={currentPage}
                pageSize={pageSize}
                scroll={{ x: 700 }}
                rowKey="email"
                showSorterTooltip={false}
                sortDirections={[]}
                onChange={(options) => {
                  setPageSize(options.pageSize);
                  setSortValue(sortValue === 'ascend' ? 'descend' : 'ascend');
                }}
              />
            </div>
          )}
        </Spin>
      </Modal>
      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        <Row gutter={[8, 8]} align="middle" justify="space-between" wrap>
          <Col xs={24} md={6}>
            <Popconfirm
              title="Delete Audiences"
              description={`Are you sure you want to delete ${selectedRows.length} selected audience${selectedRows.length !== 1 ? 's' : ''}? This action cannot be undone.`}
              onConfirm={handleBatchDelete}
              onCancel={() => {}}
              okText="Yes, Delete"
              cancelText="Cancel"
              okType="danger"
              disabled={!selectedRows.length || isTableBusy}
            >
              <Button
                size="middle"
                disabled={!selectedRows.length || isTableBusy}
                loading={isTableBusy}
                danger
                icon={<DeleteOutlined />}
              >
                Delete
              </Button>
            </Popconfirm>
          </Col>
          <Col xs={24} md={18}>
            <Space wrap size={8} style={{ width: "100%", justifyContent: "flex-end" }}>
              <Select
                placeholder="Filter by Advertiser"
                value={selectedAdvertiserId}
                onChange={(value) => setSelectedAdvertiserId(value)}
                allowClear
                style={{ minWidth: "200px" }}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {uniqueAdvertisers.map(advertiser => (
                  <Select.Option key={advertiser.id} value={advertiser.id} label={advertiser.name}>
                    {advertiser.name}
                  </Select.Option>
                ))}
              </Select>
              <Input
                placeholder="Search audiences..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
                style={{ minWidth: "200px" }}
              />
              <Space size={8} style={{ display: "inline-flex" }}>
                <Tooltip
                  title="Get the latest status of all audiences to check their progress. This will show if they are activated or still waiting for activation - waiting for device counts to populate."
                  placement="top"
                >
                  <Button
                    onClick={handleGetAllLatestStatus}
                    disabled={loadingHashedEmailList || bulkStatusLoading}
                    loading={bulkStatusLoading}
                    icon={<SyncOutlined />}
                  >
                    Get All Latest Status
                  </Button>
                </Tooltip>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => {
                    setShowGuidedWorkflow(true);
                  }}
                  disabled={uploadState && uploadState.status === "uploading"}
                >
                  Add New Audience
                </Button>
              </Space>
            </Space>
          </Col>
        </Row>
        <Row>
          <Col xs={24}>
            <Spin spinning={loadingHashedEmailList || isUploading || isTableBusy} size="large">
              {loadingHashedEmailList ||
               (!hashedEmailLists ||
                !hashedEmailLists.getHashedEmailListWithCount ||
                !hashedEmailLists.getHashedEmailListWithCount.hashedEmailList) ? (
                <div style={{ padding: "20px" }}>
                  <Skeleton active title={false} paragraph={{ rows: 8 }} />
                </div>
              ) : hashedEmailListExtracted.length > 0 ? (
                <div style={{ width: '100%', minWidth: 0 }}>
                  <Table
                    rowSelection={rowSelection}
                    loading={loadingHashedEmailList}
                    columns={columns}
                    dataSource={hashedEmailListExtracted}
                    bordered={false}
                    size={'default'}
                    rowKey="id"
                    scroll={{ x: 1460, scrollToFirstRowOnChange: true }}
                    pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      showSizeChanger: true,
                      pageSizeOptions: ['10', '20', '50', '100'],
                    }}
                    onChange={handleTableChange}
                    onRow={(record) => {
                      // Check if this is the currently uploading audience
                      const isUploading = uploadState &&
                                         uploadState.status === "uploading" &&
                                         uploadState.emailListId === record.id;

                      return {
                        onClick: () => {
                          // Prevent navigation if this audience is currently uploading
                          if (!isUploading) {
                            history.push(`/audience/details/${record.id}`);
                          }
                        },
                        style: {
                          cursor: isUploading ? "not-allowed" : "pointer",
                          opacity: isUploading ? 0.6 : 1,
                        },
                      };
                    }}
                  />
                </div>
              ) : (
                <div style={{ padding: "40px", textAlign: "center" }}>
                  <Empty
                    description={
                      searchTerm
                        ? `No audiences found matching "${searchTerm}"`
                        : hashedEmailListExtracted.length === 0
                          ? "No audiences found"
                          : `No audiences found matching "${searchTerm}"`
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                  {searchTerm && (
                    <Button
                      type="link"
                      onClick={() => setSearchTerm("")}
                      style={{ marginTop: "8px" }}
                    >
                      Clear search
                    </Button>
                  )}
                </div>
              )}
            </Spin>
          </Col>
        </Row>
      </Space>
      
      <GuidedWorkflowSteps
        visible={showGuidedWorkflow}
        onClose={() => setShowGuidedWorkflow(false)}
        createHashedEmailList={createHashedEmailList}
        updateHashedEmailList={updateHashedEmailList}
        bulkCreateHashedEmailAddress={bulkCreateHashedEmailAddress}
        activateHashedEmailList={activateHashedEmailList}
        processDeviceIds={processDeviceIds}
        refetchHashedEmailLists={refetchHashedEmailLists}
        currentOrg={currentOrg}
        providersVisible={providersVisible}
      />
      
      {/* Status Results Modal */}
      <Modal
        title="Update Status Results"
        open={statusResultsModal}
        onCancel={() => {
          setStatusResultsModal(false);
          setStatusResults(null);
        }}
        footer={[
          <Button key="close" type="primary" onClick={() => {
            setStatusResultsModal(false);
            setStatusResults(null);
          }} icon={<CloseOutlined />}>
            Close
          </Button>
        ]}
        width={900}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        {statusResults && (
          <div>
            {/* Summary Section */}
            <Card 
              style={{ 
                marginBottom: '24px',
                borderRadius: '12px',
                border: '1px solid #52c41a',
                boxShadow: '0 2px 8px rgba(82, 196, 26, 0.1)',
                background: 'linear-gradient(135deg, #f6ffed 0%, #ffffff 100%)'
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ marginBottom: '16px' }}>
                <Row align="middle" justify="space-between">
                  <Col>
                    <Title level={4} style={{ margin: 0, color: '#52c41a', fontSize: '18px', fontWeight: 600 }}>
                      <CheckCircleOutlined style={{ marginRight: '8px', fontSize: '20px' }} />
                      Summary
                    </Title>
                  </Col>
                  <Col>
                    <Badge 
                      count="Success" 
                      style={{ 
                        backgroundColor: '#52c41a',
                        fontSize: '11px',
                        height: '22px',
                        lineHeight: '22px',
                        borderRadius: '11px'
                      }}
                    />
                  </Col>
                </Row>
              </div>

              <Row gutter={[20, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <div style={{ 
                    textAlign: 'center',
                    padding: '16px 12px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #f0f0f0'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff', marginBottom: '4px' }}>
                      {statusResults.processed || 0}
                    </div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Total Processed
                    </div>
                  </div>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <div style={{ 
                    textAlign: 'center',
                    padding: '16px 12px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #f0f0f0'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a', marginBottom: '4px' }}>
                      {statusResults.successCount || 0}
                    </div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Successful Updates
                    </div>
                  </div>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <div style={{ 
                    textAlign: 'center',
                    padding: '16px 12px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #f0f0f0'
                  }}>
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: 'bold', 
                      color: (statusResults.errorCount || 0) > 0 ? '#ff4d4f' : '#52c41a',
                      marginBottom: '4px' 
                    }}>
                      {statusResults.errorCount || 0}
                    </div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Errors
                    </div>
                  </div>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <div style={{ 
                    textAlign: 'center',
                    padding: '16px 12px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #f0f0f0'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1', marginBottom: '4px' }}>
                      {Math.round(((statusResults.successCount || 0) / Math.max(statusResults.processed || 1, 1)) * 100)}%
                    </div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Success Rate
                    </div>
                  </div>
                </Col>
              </Row>

              {statusResults.message && (
                <div style={{ 
                  marginTop: '16px',
                  padding: '12px 16px',
                  backgroundColor: 'rgba(82, 196, 26, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(82, 196, 26, 0.2)'
                }}>
                  <Text style={{ 
                    color: '#52c41a',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '1.5'
                  }}>
                    <CheckCircleOutlined style={{ marginRight: '8px' }} />
                    {statusResults.message}
                  </Text>
                </div>
              )}
            </Card>
            
            {/* Audience Details Cards */}
            {statusResults.hashedEmailLists && statusResults.hashedEmailLists.length > 0 && (
              <div>
                <Title level={4} style={{ marginBottom: '16px' }}>Audience Details</Title>
                <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '8px' }}>
                  {statusResults.hashedEmailLists.map((audience, index) => {
                    // Helper functions
                    const getStatusColor = (status) => {
                      switch (status) {
                        case "UPLOADED":
                          return "processing";
                        case "WAITING_FOR_ACTIVATION":
                          return "warning";
                        case "ACTIVATED":
                          return "green";
                        case "NO_ACTION":
                          return "default";
                        default:
                          return "default";
                      }
                    };

                    const getStatusText = (status) => {
                      switch (status) {
                        case "UPLOADED":
                          return "Uploaded";
                        case "WAITING_FOR_ACTIVATION":
                          return "Waiting For Activation";
                        case "ACTIVATED":
                          return "Activated";
                        case "NO_ACTION":
                          return "No Action";
                        default:
                          return status || "Unknown";
                      }
                    };
                    
                    // Determine effective status (handle legacy activated field)
                    const effectiveStatus = audience.status || (audience.activated ? "ACTIVATED" : null);
                    
                    // Get DSP info
                    const dspName = audience.dspSegmentIds && audience.dspSegmentIds.length > 0
                      ? audience.dspSegmentIds[0].dsp
                      : audience.dsp;
                    const displayDspName = getDSPDisplayName(dspName, providersVisible);
                    
                    // Get Segment info (handle both old and new response formats)
                    let segmentId = null;
                    let segmentName = null;
                    
                    if (audience.dspSegmentIds && audience.dspSegmentIds.length > 0) {
                      segmentId = audience.dspSegmentIds[0].segmentId;
                      segmentName = audience.dspSegmentIds[0].segmentName;
                    } else {
                      segmentId = audience.segmentId && audience.segmentId.trim() !== "" ? audience.segmentId : null;
                      segmentName = audience.segmentName && audience.segmentName.trim() !== "" ? audience.segmentName : null;
                    }
                    
                    // Get created date
                    const createdAt = audience.createdAt;
                    
                    // Get process result
                    const processResult = audience.processResult;
                    const resultMessage = processResult 
                      ? (typeof processResult === 'string' ? processResult : processResult.message)
                      : null;
                    
                    return (
                      <Card
                        key={audience.id || index}
                        style={{ 
                          marginBottom: '12px',
                          borderRadius: '8px',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                          border: '1px solid #f0f0f0'
                        }}
                        bodyStyle={{ padding: '16px' }}
                      >
                        {/* Header Section */}
                        <div style={{ marginBottom: '12px' }}>
                          <Row align="middle" justify="space-between">
                            <Col>
                              <Title level={5} style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
                                {audience.name}
                              </Title>
                            </Col>
                            <Col>
                              <Space size="small">
                                <Badge 
                                  count={`${audience.stepProgress || 0}/4`} 
                                  style={{ backgroundColor: (audience.stepProgress || 0) === 4 ? '#52c41a' : '#1890ff' }}
                                />
                                <Tag 
                                  color={getStatusColor(effectiveStatus)} 
                                  style={{ 
                                    fontSize: '11px', 
                                    borderRadius: '4px',
                                    padding: '2px 8px',
                                    fontWeight: 500,
                                    backgroundColor: effectiveStatus === 'WAITING_FOR_ACTIVATION' ? '#fa8c16' : undefined,
                                    borderColor: effectiveStatus === 'WAITING_FOR_ACTIVATION' ? '#fa8c16' : undefined,
                                    color: effectiveStatus === 'WAITING_FOR_ACTIVATION' ? '#fff' : undefined
                                  }}
                                >
                                  {getStatusText(effectiveStatus)}
                                </Tag>
                              </Space>
                            </Col>
                          </Row>
                        </div>

                        {/* Details Grid */}
                        <Row gutter={[16, 8]}>
                          <Col xs={12} sm={8} md={6}>
                            <div style={{ marginBottom: '2px' }}>
                              <Text strong style={{ color: '#8c8c8c', fontSize: '11px', textTransform: 'uppercase' }}>
                                DSP Platform
                              </Text>
                            </div>
                            <div>
                              <Text style={{ fontSize: '13px', fontWeight: 500 }}>
                                {displayDspName ? displayDspName.toUpperCase() : "N/A"}
                              </Text>
                            </div>
                          </Col>
                          
                          {/* Hide Ingested Devices when stepProgress is 2 or 3 */}
                          {(audience.stepProgress !== 2 && audience.stepProgress !== 3) && (
                            <Col xs={12} sm={8} md={6}>
                              <div style={{ marginBottom: '2px' }}>
                                <Text strong style={{ color: '#8c8c8c', fontSize: '11px', textTransform: 'uppercase' }}>
                                  Ingested Devices
                                </Text>
                              </div>
                              <div>
                                <Text style={{ fontSize: '13px', fontWeight: 500 }}>
                                  {audience.devicesCount ? audience.devicesCount.toLocaleString() : (audience.deviceCount ? audience.deviceCount.toLocaleString() : "N/A")}
                                </Text>
                              </div>
                            </Col>
                          )}
                          
                          <Col xs={12} sm={8} md={6}>
                            <div style={{ marginBottom: '2px' }}>
                              <Text strong style={{ color: '#8c8c8c', fontSize: '11px', textTransform: 'uppercase' }}>
                                Created Date
                              </Text>
                            </div>
                            <div>
                              <Text style={{ fontSize: '13px', fontWeight: 500 }}>
                                {createdAt ? moment(createdAt).format("MMM DD, YYYY") : "N/A"}
                              </Text>
                            </div>
                          </Col>
                          
                          <Col xs={12} sm={8} md={6}>
                            <div style={{ marginBottom: '2px' }}>
                              <Text strong style={{ color: '#8c8c8c', fontSize: '11px', textTransform: 'uppercase' }}>
                                Data Type
                              </Text>
                            </div>
                            <div>
                              <Text style={{ fontSize: '13px', fontWeight: 500 }}>
                                {audience.typeOfFirstPartyData === "phones" ? "Phone Numbers" : audience.typeOfFirstPartyData === "lookalike" ? "Lookalike Audiences" : "Email Addresses"}
                              </Text>
                            </div>
                          </Col>

                          {/* Show Segment Name only when stepProgress is 3 or 4 */}
                          {(audience.stepProgress === 3 || audience.stepProgress === 4) && (
                            <Col xs={12} sm={8} md={6}>
                              <div style={{ marginBottom: '2px' }}>
                                <Text strong style={{ color: '#8c8c8c', fontSize: '11px', textTransform: 'uppercase' }}>
                                  Segment Name
                                </Text>
                              </div>
                              <div>
                                <Text style={{ fontSize: '13px', fontWeight: 500 }}>
                                  {segmentName || "N/A"}
                                </Text>
                              </div>
                            </Col>
                          )}
                        </Row>

                        {/* Segment ID Section (if available) */}
                        {segmentId && (
                          <div style={{ marginTop: '12px', paddingTop: '8px', borderTop: '1px solid #f5f5f5' }}>
                            <div style={{ marginBottom: '4px' }}>
                              <Text strong style={{ color: '#8c8c8c', fontSize: '11px', textTransform: 'uppercase' }}>
                                Segment ID
                              </Text>
                            </div>
                            <div style={{ 
                              backgroundColor: '#fafafa', 
                              padding: '8px', 
                              borderRadius: '4px',
                              border: '1px solid #f0f0f0'
                            }}>
                              <Text 
                                code 
                                style={{ 
                                  fontSize: '12px',
                                  wordBreak: 'break-all',
                                  backgroundColor: 'transparent',
                                  padding: 0,
                                  border: 'none'
                                }}
                              >
                                {segmentId}
                              </Text>
                            </div>
                          </div>
                        )}

                        {/* Message Section (handle new response format) */}
                        {(resultMessage || audience.message) && (
                          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f5f5f5' }}>
                            <div style={{ marginBottom: '4px' }}>
                              <Text strong style={{ color: '#8c8c8c', fontSize: '11px', textTransform: 'uppercase' }}>
                                Status Message
                              </Text>
                            </div>
                            <div>
                              <Text style={{ 
                                color: '#666', 
                                fontSize: '12px', 
                                fontStyle: 'italic',
                                lineHeight: '1.4'
                              }}>
                                {audience.message || resultMessage}
                              </Text>
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
                
                {/* Show total count */}
                <div style={{ 
                  textAlign: 'center', 
                  marginTop: '12px', 
                  padding: '8px',
                  backgroundColor: '#fafafa',
                  borderRadius: '6px'
                }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Showing {statusResults.hashedEmailLists.length} audience{statusResults.hashedEmailLists.length !== 1 ? 's' : ''}
                  </Text>
                </div>
              </div>
            )}
            
            {(!statusResults.hashedEmailLists || statusResults.hashedEmailLists.length === 0) && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Empty description="No detailed results available" />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Process Model Score Modal */}
      <Modal
        title="Process Model Score Results"
        open={processModelScoreModal}
        onCancel={() => {
          setProcessModelScoreModal(false);
          setModelScoreResults(null);
        }}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => {
              setProcessModelScoreModal(false);
              setModelScoreResults(null);
            }}
            icon={<CloseOutlined />}
          >
            Close
          </Button>
        ]}
        width={700}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        {modelScoreResults && (
          <div>
            {/* Results Card */}
            <Card
              style={{
                marginBottom: '24px',
                borderRadius: '12px',
                border: modelScoreResults.success ? '1px solid #52c41a' : '1px solid #ff4d4f',
                boxShadow: modelScoreResults.success
                  ? '0 2px 8px rgba(82, 196, 26, 0.1)'
                  : '0 2px 8px rgba(255, 77, 79, 0.1)',
                background: modelScoreResults.success
                  ? 'linear-gradient(135deg, #f6ffed 0%, #ffffff 100%)'
                  : 'linear-gradient(135deg, #fff1f0 0%, #ffffff 100%)'
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ marginBottom: '16px' }}>
                <Row align="middle" justify="space-between">
                  <Col>
                    <Title
                      level={4}
                      style={{
                        margin: 0,
                        color: modelScoreResults.success ? '#52c41a' : '#ff4d4f',
                        fontSize: '18px',
                        fontWeight: 600
                      }}
                    >
                      {modelScoreResults.success ? (
                        <>
                          <CheckCircleOutlined style={{ marginRight: '8px', fontSize: '20px' }} />
                          Success
                        </>
                      ) : (
                        <>
                          <CloseOutlined style={{ marginRight: '8px', fontSize: '20px' }} />
                          Error
                        </>
                      )}
                    </Title>
                  </Col>
                  <Col>
                    <Badge
                      count={modelScoreResults.success ? "Completed" : "Failed"}
                      style={{
                        backgroundColor: modelScoreResults.success ? '#52c41a' : '#ff4d4f',
                        fontSize: '11px',
                        height: '22px',
                        lineHeight: '22px',
                        borderRadius: '11px'
                      }}
                    />
                  </Col>
                </Row>
              </div>

              {/* Message */}
              {modelScoreResults.message && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px 16px',
                  backgroundColor: modelScoreResults.success
                    ? 'rgba(82, 196, 26, 0.05)'
                    : 'rgba(255, 77, 79, 0.05)',
                  borderRadius: '8px',
                  border: modelScoreResults.success
                    ? '1px solid rgba(82, 196, 26, 0.2)'
                    : '1px solid rgba(255, 77, 79, 0.2)'
                }}>
                  <Text style={{
                    color: modelScoreResults.success ? '#52c41a' : '#ff4d4f',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '1.5'
                  }}>
                    {modelScoreResults.success ? (
                      <CheckCircleOutlined style={{ marginRight: '8px' }} />
                    ) : (
                      <CloseOutlined style={{ marginRight: '8px' }} />
                    )}
                    {modelScoreResults.message}
                  </Text>
                </div>
              )}

              {/* Statistics - only show if success and data exists */}
              {modelScoreResults.success && modelScoreResults.data && (
                <>
                  <Row gutter={[20, 16]}>
                    <Col xs={24} sm={12} md={8}>
                      <div style={{
                        textAlign: 'center',
                        padding: '16px 12px',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #f0f0f0'
                      }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff', marginBottom: '4px' }}>
                          {modelScoreResults.data.totalProcessed?.toLocaleString() || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Total Processed
                        </div>
                      </div>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                      <div style={{
                        textAlign: 'center',
                        padding: '16px 12px',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #f0f0f0'
                      }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a', marginBottom: '4px' }}>
                          {modelScoreResults.data.filteredIdsCount?.toLocaleString() || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Records Kept (Score ≥ 80)
                        </div>
                      </div>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                      <div style={{
                        textAlign: 'center',
                        padding: '16px 12px',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #f0f0f0'
                      }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1', marginBottom: '4px' }}>
                          {modelScoreResults.data.percentageKept || '0'}%
                        </div>
                        <div style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Percentage Kept
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {/* Additional Details Section */}
                  <div style={{ marginTop: '20px' }}>
                    <Divider style={{ margin: '16px 0' }} />

                    {/* Status and Job Information */}
                    <Row gutter={[16, 12]} style={{ marginBottom: '12px' }}>
                      <Col xs={24} sm={12}>
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#fafafa',
                          borderRadius: '6px',
                          border: '1px solid #f0f0f0'
                        }}>
                          <Text strong style={{ color: '#8c8c8c', fontSize: '11px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                            Status
                          </Text>
                          <Tag
                            color={modelScoreResults.data.status === 'completed' ? 'success' : 'processing'}
                            style={{ fontSize: '12px', fontWeight: 500 }}
                          >
                            {modelScoreResults.data.status?.toUpperCase() || 'UNKNOWN'}
                          </Tag>
                        </div>
                      </Col>

                      <Col xs={24} sm={12}>
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#fafafa',
                          borderRadius: '6px',
                          border: '1px solid #f0f0f0'
                        }}>
                          <Text strong style={{ color: '#8c8c8c', fontSize: '11px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                            Job ID
                          </Text>
                          <Text code style={{ fontSize: '13px', fontWeight: 500 }}>
                            {modelScoreResults.data.jobId || 'N/A'}
                          </Text>
                        </div>
                      </Col>
                    </Row>

                    {/* Already Processed Status */}
                    {modelScoreResults.data.alreadyProcessed !== undefined && (
                      <div style={{
                        padding: '12px',
                        backgroundColor: modelScoreResults.data.alreadyProcessed ? '#fff7e6' : '#f6ffed',
                        borderRadius: '6px',
                        border: modelScoreResults.data.alreadyProcessed ? '1px solid #ffd591' : '1px solid #b7eb8f',
                        marginBottom: '12px'
                      }}>
                        <Text strong style={{ color: '#8c8c8c', fontSize: '11px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                          Processing Status
                        </Text>
                        <Text style={{ fontSize: '13px', fontWeight: 500, color: modelScoreResults.data.alreadyProcessed ? '#fa8c16' : '#52c41a' }}>
                          {modelScoreResults.data.alreadyProcessed ? (
                            <>
                              <InfoCircleOutlined style={{ marginRight: '6px' }} />
                              Already Processed Previously
                            </>
                          ) : (
                            <>
                              <CheckCircleOutlined style={{ marginRight: '6px' }} />
                              Newly Processed
                            </>
                          )}
                        </Text>
                      </div>
                    )}

                    {/* S3 Path */}
                    {modelScoreResults.data.s3Path && (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#fafafa',
                        borderRadius: '6px',
                        border: '1px solid #f0f0f0'
                      }}>
                        <Text strong style={{ color: '#8c8c8c', fontSize: '11px', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                          S3 Upload Path
                        </Text>
                        <Text
                          code
                          style={{
                            fontSize: '11px',
                            wordBreak: 'break-all',
                            backgroundColor: 'transparent',
                            padding: 0,
                            border: 'none',
                            display: 'block'
                          }}
                        >
                          {modelScoreResults.data.s3Path}
                        </Text>
                      </div>
                    )}
                  </div>
                </>
              )}
            </Card>

            {/* Empty state for no data */}
            {!modelScoreResults.success && !modelScoreResults.data && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Empty
                  description="Processing failed. Please check the error message above and try again."
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default EmailTab;
